import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Building2,
  ChevronsUpDown,
  FileText,
  LayoutGrid,
  LogOut,
  MapPin,
  Megaphone,
  Monitor,
  Moon,
  ScrollText,
  ShieldCheck,
  Sun,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import { getInitials } from '@/lib/utils';

/**
 * Navigation is grouped by what an operator is doing, not by data model:
 * "Media" is the sell side they edit daily, "Network" is the configuration they
 * touch during onboarding, "Compliance" is the evidence they only open when
 * someone asks a hard question.
 */
const ADMIN_NAV_GROUPS = [
  {
    label: 'Media',
    items: [
      { to: '/', label: 'Overview', icon: LayoutGrid, exact: true },
      { to: '/campaigns', label: 'Campaigns', icon: Megaphone },
      { to: '/reporting', label: 'Reporting', icon: BarChart3 },
    ],
  },
  {
    label: 'Network',
    items: [
      { to: '/sponsors', label: 'Sponsors', icon: Building2 },
      { to: '/placements', label: 'Placements', icon: Monitor },
      { to: '/locations', label: 'Locations', icon: MapPin },
      { to: '/guides', label: 'Guide pages', icon: FileText },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/consent', label: 'Consent ledger', icon: ShieldCheck },
      { to: '/audit', label: 'Audit log', icon: ScrollText },
      { to: '/team', label: 'Team', icon: Users },
    ],
  },
] as const;

/**
 * The sponsor portal's entire navigation — read-only, and deliberately this
 * short. There is no campaign detail page (nothing here but operator levers)
 * and nothing from Network or Compliance: a sponsor sees their own delivery,
 * not DearDoc's practice roster or the audit trail.
 */
const PORTAL_NAV_GROUPS = [
  {
    label: 'Your account',
    items: [
      { to: '/portal', label: 'Overview', icon: LayoutGrid, exact: true },
      { to: '/portal/reporting', label: 'Reporting', icon: BarChart3 },
    ],
  },
] as const;

interface AppSidebarProps {
  variant: 'admin' | 'portal';
}

export function AppSidebar({ variant }: AppSidebarProps) {
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navGroups = variant === 'portal' ? PORTAL_NAV_GROUPS : ADMIN_NAV_GROUPS;
  const homeTo = variant === 'portal' ? '/portal' : '/';

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to={homeTo}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <Megaphone className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Media Network</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {variant === 'portal' ? 'Sponsor portal' : 'DearDoc'}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.label}
                      isActive={isActive(item.to, 'exact' in item ? item.exact : false)}
                    >
                      <Link to={item.to}>
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg text-xs">
                      {getInitials(user?.displayName, user?.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">
                      {user?.displayName ?? user?.email ?? 'Signed out'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.isAdmin ? 'dmn.admin' : user?.sponsorId ? 'sponsor' : 'no role'}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <DropdownMenuLabel className="truncate font-normal">
                  {user?.email ?? '—'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  Theme
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="size-4" />
                  Light {theme === 'light' && '·'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="size-4" />
                  Dark {theme === 'dark' && '·'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="size-4" />
                  System {theme === 'system' && '·'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void signOut()}>
                  <LogOut className="size-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
